import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  Clock,
  MapPin,
  Layers,
  Scissors,
  Wrench,
  Home,
  ArrowRight,
  Ruler,
  Building2,
  Building,
  House,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison sur-mesure au Pecq ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison sur les rives du Pecq
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Une livraison de cartons sur-mesure et rapide sur les rives
              du Pecq
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la préparation de vos colis nécessite de
              l&apos;énergie et du temps, notre service logistique de
              proximité prend en charge l&apos;acheminement de vos
              fournitures directement chez vous. Nous livrons
              l&apos;intégralité de votre commande à plat {city.aCity}
              {" "}ainsi que dans les communes voisines (Saint-Germain-en-Laye,
              Le Mesnil-le-Roi, Montesson, Marly-le-Roi). Toutes vos
              boîtes professionnelles arrivent impeccablement propres,
              sèches et prêtes à l&apos;emploi sur un créneau horaire
              précis fixé selon vos disponibilités, vous évitant ainsi
              de perdre votre journée à attendre.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Livraison à plat directement chez vous",
                body: "Vos cartons arrivent au pied de votre immeuble ou de votre maison au Pecq, sans aucune manutention de votre part.",
                Icon: MapPin,
              },
              {
                title: "Communes voisines couvertes",
                body: "Nous livrons aussi Saint-Germain-en-Laye, Le Mesnil-le-Roi, Montesson et Marly-le-Roi sur le même circuit.",
                Icon: Truck,
              },
              {
                title: "Créneau précis & matériel sec",
                body: "Vous choisissez votre horaire, nous le tenons. Vos cartons restent propres, secs et 100 % rigides à la livraison.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Tout le matériel réuni ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Boutique tout-en-un
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Tout le matériel réuni au même endroit
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne perdez plus votre temps et votre énergie à courir les
            magasins de bricolage ou à multiplier les commandes sur
            différents sites web. Notre boutique en ligne centralise
            l&apos;intégralité des équipements indispensables pour
            réussir votre projet de A à Z.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Cartonnerie */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Boxes className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                La cartonnerie spécialisée
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Du modèle standard au carton penderie pour vos vêtements,
              en passant par les bacs renforcés pour les livres et les
              emballages à croisillons pour votre vaisselle fragile.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-standard"
              >
                Cartons standards
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-penderie"
              >
                Carton penderie
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-livres"
              >
                Bacs livres renforcés
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-croisillons"
              >
                Croisillons vaisselle
              </Link>
            </div>
          </div>

          {/* Protection */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Layers className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Les fournitures de protection
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Des rouleaux de film à bulles haute densité, du papier
              kraft de calage et des housses étanches pour protéger
              vos matelas et canapés de la poussière.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/emballage-protection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-bulles"
              >
                Film à bulles
              </Link>
              <Link
                to="/emballage-protection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-kraft"
              >
                Papier kraft
              </Link>
              <Link
                to="/emballage-protection"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-housses"
              >
                Housses étanches
              </Link>
            </div>
          </div>

          {/* Manutention */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Wrench className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                Les accessoires de manutention
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Des rouleaux d&apos;adhésif industriel, des dévidoirs
              pistolets ergonomiques, des marqueurs biseautés, des
              cutters sécurisés et des chariots à roulettes pour
              préserver votre dos.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-adhesif"
              >
                Adhésif industriel
              </Link>
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-pistolet"
              >
                Dévidoir pistolet
              </Link>
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-cutter"
              >
                Cutters sécurisés
              </Link>
              <Link
                to="/fermeture-accessoires"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12px] font-bold text-[#0f1d2b] transition-colors"
                data-testid="city-le-pecq-chip-chariot"
              >
                Chariots à roulettes
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-pecq-cta-cartons"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-pecq-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les kits clé-en-main
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Combien de cartons prévoir (NOUVEAU) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Guide volume
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Combien de cartons prévoir pour votre logement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Éviter le manque de cartons le dernier week-end des
            préparatifs est la clé d&apos;un déménagement sans stress.
            Voici une estimation moyenne du volume d&apos;emballages
            professionnels à prévoir selon la taille de votre
            habitation.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            {
              tag: "MOINS DE 30 m²",
              title: "Studio / T1",
              Icon: Home,
              accent: "#237cb5",
              items: [
                "10 à 15 cartons standards",
                "5 cartons pour livres (charges lourdes)",
                "1 rouleau d'adhésif",
              ],
            },
            {
              tag: "30 À 50 m²",
              title: "Appartement T2",
              Icon: Building,
              accent: "#237cb5",
              items: [
                "20 à 25 cartons standards",
                "10 cartons livres",
                "1 carton penderie",
                "2 rouleaux d'adhésif",
              ],
            },
            {
              tag: "50 À 70 m²",
              title: "Appartement T3",
              Icon: Building2,
              accent: "#FF5722",
              items: [
                "30 à 40 cartons standards",
                "15 cartons livres",
                "2 cartons penderies",
                "Du film à bulles",
                "3 rouleaux d'adhésif",
              ],
            },
            {
              tag: "+ DE 80 m²",
              title: "Maison / F4 et plus",
              Icon: House,
              accent: "#FF5722",
              items: [
                "50 à 70 cartons standards (minimum)",
                "25 cartons livres",
                "3 à 4 cartons penderies",
                "Cartons à croisillons vaisselle",
                "Kit complet de fermeture (pistolet + adhésifs)",
              ],
            },
          ].map(({ tag, title, Icon, accent, items }) => (
            <div
              key={title}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 overflow-hidden"
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <div className="flex items-center gap-3">
                <span
                  className="flex items-center justify-center h-12 w-12 rounded-xl text-white"
                  style={{ backgroundColor: accent }}
                >
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <div>
                  <div
                    className="text-[10.5px] tracking-[0.18em] uppercase font-extrabold"
                    style={{ color: accent }}
                  >
                    {tag}
                  </div>
                  <h3 className="font-display font-extrabold text-[19px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5">
                {items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-[14px] text-[#3a4856] leading-relaxed"
                  >
                    <Ruler
                      className="h-4 w-4 mt-0.5 shrink-0"
                      style={{ color: accent }}
                      strokeWidth={2.6}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-pecq-cta-kits-bottom"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les kits par taille de logement
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-pecq-cta-fermeture"
          >
            <Scissors className="h-4 w-4" strokeWidth={2.6} />
            Accessoires de fermeture
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-le-pecq-cta-back-dept"
        >
          <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
